import { CommonConstants } from "@assets/constants";
import { RegistryTypes, PortTypes } from "@assets/types";

import { ArrayUtilities } from ".";

/** The values to determine if a specific incoterm place can be selected or not. */
export interface Places {
  PLR: PortTypes.Port | null;
  POL: PortTypes.Port | null;
  PFD: PortTypes.Port | null;
  POD: PortTypes.Port | null;
}

/** Get the possible selectable incoterms based on the filled in PLR, POL, PFD, POD ports.
 * @param allIncoterms All incoterms array
 * @param places Object with the values of PLR, POL, PFD, POD port values from the form
 * @returns The incoterms that can be selected based on the filled in information
 */
const getPossibleIncoterms = (
  allIncoterms: RegistryTypes.Incoterm[],
  places: Places
): RegistryTypes.Incoterm[] => {
  const plrOnlyCondition = (it: RegistryTypes.Incoterm) =>
    it.AllowedIncotermsPoints.length === 1 &&
    it.AllowedIncotermsPoints[0] === CommonConstants.INCOTERM_POINT.PLR;

  const pfdOnlyCondition = (it: RegistryTypes.Incoterm) =>
    it.AllowedIncotermsPoints.length === 1 &&
    it.AllowedIncotermsPoints[0] === CommonConstants.INCOTERM_POINT.PFD;

  // Filter out either plr only or pfd only
  if (!places.PLR && !places.PFD) {
    return allIncoterms.filter(
      (it: RegistryTypes.Incoterm) =>
        !(plrOnlyCondition(it) || pfdOnlyCondition(it))
    );
  }

  // Filter out plr only
  if (!places.PLR) {
    return allIncoterms.filter(
      (it: RegistryTypes.Incoterm) => !plrOnlyCondition(it)
    );
  }

  // Filter out pfd only
  if (!places.PFD) {
    return allIncoterms.filter(
      (it: RegistryTypes.Incoterm) => !pfdOnlyCondition(it)
    );
  }
  return allIncoterms;
};

export interface GetFilteredIncoterms {
  wasSelectedRemoved: boolean;
  filteredIncoterms: RegistryTypes.Incoterm[];
}

/**
 * Get a list with with the incoterms that depend only on PLR/PFD
 * but they are not filled out in the form
 * and reset the value if it was previously selected
 * @param allIncoterms All incoterms array
 * @param places Object with the values of PLR, POL, PFD, POD port values from the form
 * @param selectedIncotermCode The current selected incoterm
 * @returns Filtered array of incoterms that can be selected
 */
export const getFilteredIncoterms = (
  allIncoterms: RegistryTypes.Incoterm[],
  places: Places,
  selectedIncotermCode: string | null
): GetFilteredIncoterms => {
  const filteredIncoterms: RegistryTypes.Incoterm[] = getPossibleIncoterms(
    allIncoterms,
    places
  );

  return {
    wasSelectedRemoved:
      selectedIncotermCode !== null &&
      filteredIncoterms.filter(
        (it: RegistryTypes.Incoterm) => it.TermsCode === selectedIncotermCode
      ).length === 0,
    filteredIncoterms,
  };
};

/**
 * Get the incoterm place based on the selected incoterm and it's PrimaryIncotermPoint and AllowedIncotermsPoints
 * @param selectedIncoterm The selected incoterm
 * @param places Object with PLR, POL, PFD, POD selected port values
 * @returns The possible incoterm place ports
 */
export const getIncotermPorts = (
  selectedIncoterm: RegistryTypes.Incoterm | null,
  places: Places
): PortTypes.Port[] => {
  let incotermsPointList: CommonConstants.INCOTERM_POINT[] = [];
  if (selectedIncoterm) {
    // If the selected incoterm has PrimaryIncotermPoint defined
    // and it is filled in the form, the incoterm place is set to that one
    if (
      selectedIncoterm.PrimaryIncotermsPoint &&
      places[selectedIncoterm.PrimaryIncotermsPoint]
    ) {
      incotermsPointList.push(selectedIncoterm.PrimaryIncotermsPoint);
    } else {
      // If the incoterm has no PrimaryIncotermPoint defined or filled out in the form
      // the incoterm place value is taken from the AllowedIncotermsPoints that have values
      incotermsPointList = selectedIncoterm.AllowedIncotermsPoints.filter(
        (incotermPoint: CommonConstants.INCOTERM_POINT) => places[incotermPoint]
      );
    }

    // Remove port duplicates
    return ArrayUtilities.uniqueBy(
      incotermsPointList
        .map((pointCode: CommonConstants.INCOTERM_POINT) => places[pointCode])
        .filter((x): x is PortTypes.Port => Boolean(x)),
      "PointCode"
    );
  }
  return [];
};
